import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";
import useBulkSelection from "@/hooks/useBulkSelection";
import useInlineBulkEdit from "@/hooks/useInlineBulkEdit";
import { EditableText, EditableSelect, EditableDate } from "@/components/EditableCell";
import RevenueLedgerRow, { STICKY_W, COL_W, CURRENCIES } from "@/components/RevenueLedgerRow";
import BulkSaveBar from "@/components/BulkSaveBar";
import GlobalInvoiceWizard from "@/components/GlobalInvoiceWizard";
import { FilterBar, FilterField, FilterDate } from "@/components/FinanceFilterBar";
import FinanceTotalsBar from "@/components/FinanceTotalsBar";
import { invoiceRevenueINR, invoiceOutstandingINR, INVOICE_STATUSES, matchesMonth, sortSources } from "@/lib/finance";
import { useAuth } from "@/context/AuthContext";

const EMPTY_FILTERS = {
  dateFrom: "", dateTo: "", invoiceNumber: "", clientId: "all", projectId: "all",
  serviceType: "all", hsn: "", currency: "all", gstMin: "", gstMax: "",
  status: "all", dueFrom: "", dueTo: "", compliance: "all",
};

// Phase 94 — every visible column is an inline-editable cell; the pencil
// "Edit dialog" is gone. Edit any cell → row highlights orange → hit
// "Bulk Save Changes" once to commit every pending edit in one request.
// Phase 96 — renamed from InvoicesLedger → RevenueLedger ("Invoices Ledger"
// tab is now "Revenue" everywhere). Backing collection renamed
// finance_invoices → finance_revenue. Client + Project are now strict
// dropdowns pulled from the Registry / active project list — no more free
// text, so ledger rows always resolve to a real client/project record.
// Phase 140 — new invoices are created exclusively through the shared
// GlobalInvoiceWizard (Draft -> Submit for Approval -> Founder Approve);
// the old pinned inline "add row" is gone. Existing rows keep their
// inline-cell editing for quick corrections.
export default function RevenueLedger({ rows, categories, onChanged, month = "all", presetFilters, quickFilter, onClearFilter }) {
  const { user } = useAuth();
  const isFounder = user?.role === "founder";
  const [working, setWorking] = useState(null); // invoice id currently submitting/approving
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mains, setMains] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fircPreview, setFircPreview] = useState(null); // { url, title }
  const [sources, setSources] = useState([]);
  const [clientsError, setClientsError] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Feb 2026 — True Received Date: flipping a row's status to Received/Paid
  // without a payment_received_date already set opens this prompt so the
  // real bank-hit date is always captured, not just assumed.
  const [receivedPrompt, setReceivedPrompt] = useState(null); // { id, status }
  const [receivedDateInput, setReceivedDateInput] = useState("");

  // Phase 122 — merge the Finance page's Global Filter Bar straight into
  // this ledger's own Advanced Filter Bar whenever it changes.
  useEffect(() => {
    if (presetFilters) setFilters((prev) => ({ ...prev, ...presetFilters }));
  }, [presetFilters]);

  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => { setClients(data || []); setClientsError(null); })
      .catch((e) => { setClients([]); setClientsError(e?.response?.status === 403 ? "denied" : "error"); });
    api.get("/daily-tasks/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
    api.get("/finance/sources").then(({ data }) => setSources(data || [])).catch(() => setSources([]));
    api.get("/finance/revenue-categories").then(({ data }) => setMains(data?.mains || [])).catch(() => setMains([]));
  }, []);
  useEffect(() => {
    // Phase 138 — surface the denial instead of leaving the Client picker
    // silently empty inside this compact table's cells.
    if (clientsError === "denied") toast.error("Access denied — couldn't load the Client list. Ask a Founder/Admin for access.");
  }, [clientsError]);

  const monthFiltered = useMemo(() => (rows || []).filter((r) => matchesMonth(r, month)), [rows, month]);
  const quickFiltered = useMemo(() => {
    if (!quickFilter?.statuses) return monthFiltered;
    return monthFiltered.filter((r) => quickFilter.statuses.includes((r.status || "").toLowerCase()));
  }, [monthFiltered, quickFilter]);

  // Phase 97 Batch 2 — Advanced Filtering Engine (Revenue): Date Range,
  // Invoice #, Client, Project, Service Type, HSN, Currency, GST %,
  // Status, Due Date, Compliance (FIRC).
  const activeFilterCount = useMemo(() => Object.entries(filters).filter(([, v]) => v && v !== "all").length, [filters]);
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const filteredRows = useMemo(() => {
    return quickFiltered.filter((r) => {
      const d = r.accrual_date || r.date || "";
      if (filters.dateFrom && d && d < filters.dateFrom) return false;
      if (filters.dateTo && d && d > filters.dateTo) return false;
      if (filters.invoiceNumber && !(r.invoice_number || "").toLowerCase().includes(filters.invoiceNumber.toLowerCase())) return false;
      if (filters.clientId !== "all" && r.client_id !== filters.clientId) return false;
      if (filters.projectId !== "all" && r.project_id !== filters.projectId) return false;
      if (filters.serviceType !== "all" && (r.service_type || "Unspecified") !== filters.serviceType) return false;
      if (filters.hsn && !(r.hsn_code || "").toLowerCase().includes(filters.hsn.toLowerCase())) return false;
      if (filters.currency !== "all" && (r.currency || "INR") !== filters.currency) return false;
      if (filters.gstMin !== "" && Number(r.gst || 0) < Number(filters.gstMin)) return false;
      if (filters.gstMax !== "" && Number(r.gst || 0) > Number(filters.gstMax)) return false;
      if (filters.status !== "all" && (r.status || "").toLowerCase() !== filters.status) return false;
      if (filters.dueFrom && r.due_date && r.due_date < filters.dueFrom) return false;
      if (filters.dueTo && r.due_date && r.due_date > filters.dueTo) return false;
      if (filters.compliance === "missing" && !r.needs_firc) return false;
      if (filters.compliance === "complete" && r.needs_firc) return false;
      return true;
    });
  }, [quickFiltered, filters]);

  const { rows: editRows, updateCell, isDirty, dirtyCount, discardAll, bulkSave, saving } = useInlineBulkEdit({
    sourceRows: filteredRows,
    endpoint: "/finance/invoices/bulk",
    numericFields: ["amount", "gst", "tds", "fx_rate", "net_received_inr"],
    onSaved: onChanged,
  });

  const sorted = useMemo(() => [...editRows].sort((a, b) => ((b.accrual_date || b.date || "")).localeCompare(a.accrual_date || a.date || "")), [editRows]);
  const sortedIds = useMemo(() => sorted.map((r) => r.id), [sorted]);
  const bulk = useBulkSelection({ collection: "finance_revenue", ids: sortedIds, onDone: onChanged });

  const remove = useCallback(async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/finance/invoices/${id}`); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [onChanged]);

  // Phase 97 Batch 2 — Inter-Ledger Movement: correct an accidental Revenue
  // entry by moving it into Expenses. One-directional only — Expenses never
  // move back into Revenue.
  const moveToExpense = useCallback(async (row) => {
    if (!window.confirm(`Move ${row.invoice_number || "this invoice"} to Expenses? It will be removed from Revenue.`)) return;
    try {
      await api.post(`/finance/invoices/${row.id}/move-to-expense`);
      toast.success("Moved to Expenses");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [onChanged]);

  // Phase 140 — two-step Founder approval: any Finance user can Submit a
  // Draft (once HSN + drive_link are present) for approval; only a Founder
  // can then Approve it into "Approved / Ready for Zoho".
  const submitForApproval = useCallback(async (row) => {
    setWorking(row.id);
    try {
      await api.post(`/finance/invoices/${row.id}/submit-for-approval`);
      toast.success(`${row.invoice_number} submitted for Founder approval`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(null); }
  }, [onChanged]);
  const approve = useCallback(async (row) => {
    setWorking(row.id);
    try {
      await api.post(`/finance/invoices/${row.id}/approve`);
      toast.success(`${row.invoice_number} approved — Ready for Zoho`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(null); }
  }, [onChanged]);
  const onPreview = useCallback((p) => setFircPreview(p), []);

  const dlTemplate = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/finance/invoices/template`, "_blank");
  const misExport = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/finance/mis-export`, "_blank");
  const onBulkPick = () => fileRef.current?.click();
  const onBulkFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/finance/invoices/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} invoices${data.errors?.length ? ` · ${data.errors.length} row errors` : ""}`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const clientOptions = useMemo(() => clients.map((c) => ({ value: c.id, label: c.company || c.name })), [clients]);
  const projectOptions = useMemo(() => projects.map((p) => ({ value: p.id, label: p.name })), [projects]);
  const sourceOptions = useMemo(() => sortSources(sources).map((s) => ({ value: s.id, label: s.name })), [sources]);

  // Feb 2026 — Admin Requirement: a status change into Received/Paid must
  // carry the True Received Date. If the row doesn't already have one
  // staged/saved, hold the status change and prompt for it first.
  const handleStatusChange = useCallback((row, nextValue) => {
    const becomingReceived = ["received", "paid"].includes(nextValue) && !["received", "paid"].includes((row.status || "").toLowerCase());
    if (becomingReceived && !row.payment_received_date) {
      setReceivedDateInput("");
      setReceivedPrompt({ id: row.id, status: nextValue });
      return;
    }
    updateCell(row.id, "status", nextValue);
  }, [updateCell]);
  const confirmReceivedPrompt = () => {
    if (!receivedDateInput) { toast.error("Pick the date the payment actually landed in the bank"); return; }
    updateCell(receivedPrompt.id, "payment_received_date", receivedDateInput);
    updateCell(receivedPrompt.id, "status", receivedPrompt.status);
    setReceivedPrompt(null);
    setReceivedDateInput("");
  };

  return (
    <div data-testid="revenue-ledger">
      <BulkSaveBar count={dirtyCount} saving={saving} onSave={bulkSave} onDiscard={discardAll} testIdPrefix="revenue-ledger" raised={bulk.selected.size > 0} />

      {quickFilter?.statuses && (
        <div className="flex items-center gap-2 mb-3 text-xs" data-testid="revenue-ledger-quick-filter">
          <span className="px-2 py-1 rounded-sm bg-orange-100 text-[#F54800] font-medium inline-flex items-center gap-1">
            Filtered: {quickFilter.label}
            <button onClick={onClearFilter} data-testid="revenue-ledger-clear-filter"><X size={12} /></button>
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs text-slate-500">{sorted.length} invoice{sorted.length === 1 ? "" : "s"}</div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onBulkFile} data-testid="revenue-ledger-bulk-file" />
          <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={misExport} data-testid="revenue-ledger-mis-export-btn">
            <Download size={12} className="mr-1" /> Bulk export (MIS)
          </Button>
          <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={dlTemplate} data-testid="revenue-ledger-template-btn">
            <Download size={12} className="mr-1" /> Template
          </Button>
          <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={onBulkPick} disabled={uploading} data-testid="revenue-ledger-bulk-btn">
            <FileSpreadsheet size={12} className="mr-1" /> {uploading ? "Uploading…" : "Bulk import"}
          </Button>
          <GlobalInvoiceWizard onSaved={onChanged} trigger={
            <Button size="sm" className="rounded-sm h-8 text-xs bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="revenue-ledger-new-invoice-btn">
              New Invoice
            </Button>
          } />
        </div>
      </div>

      <FilterBar open={filterOpen} onToggle={() => setFilterOpen((o) => !o)} count={activeFilterCount} onClear={clearFilters} testId="revenue-filter-bar">
        <FilterField label="Date range">
          <div className="flex items-center gap-1">
            <FilterDate value={filters.dateFrom} onChange={(v) => setFilters({ ...filters, dateFrom: v })} placeholder="From" testId="revenue-filter-date-from" />
            <FilterDate value={filters.dateTo} onChange={(v) => setFilters({ ...filters, dateTo: v })} placeholder="To" testId="revenue-filter-date-to" />
          </div>
        </FilterField>
        <FilterField label="Invoice #">
          <EditableText className="w-28 !h-8 !border-slate-200 bg-white" placeholder="Search" value={filters.invoiceNumber} onChange={(v) => setFilters({ ...filters, invoiceNumber: v })} testId="revenue-filter-invoice-number" />
        </FilterField>
        <FilterField label="Client">
          <EditableSelect value={filters.clientId} onChange={(v) => setFilters({ ...filters, clientId: v })} options={[{ value: "all", label: "All clients" }, ...clientOptions]} testId="revenue-filter-client" />
        </FilterField>
        <FilterField label="Project">
          <EditableSelect value={filters.projectId} onChange={(v) => setFilters({ ...filters, projectId: v })} options={[{ value: "all", label: "All projects" }, ...projectOptions]} testId="revenue-filter-project" />
        </FilterField>
        <FilterField label="Service type">
          <EditableSelect value={filters.serviceType} onChange={(v) => setFilters({ ...filters, serviceType: v })} options={[{ value: "all", label: "All types" }, ...mains.map((s) => ({ value: s, label: s }))]} testId="revenue-filter-service-type" />
        </FilterField>
        <FilterField label="HSN #">
          <EditableText className="w-24 !h-8 !border-slate-200 bg-white" placeholder="HSN" value={filters.hsn} onChange={(v) => setFilters({ ...filters, hsn: v })} testId="revenue-filter-hsn" />
        </FilterField>
        <FilterField label="Currency">
          <EditableSelect value={filters.currency} onChange={(v) => setFilters({ ...filters, currency: v })} options={[{ value: "all", label: "All" }, ...CURRENCIES.map((c) => ({ value: c, label: c }))]} testId="revenue-filter-currency" />
        </FilterField>
        <FilterField label="GST % range">
          <div className="flex items-center gap-1">
            <EditableText type="number" className="w-14 !h-8 !border-slate-200 bg-white" placeholder="Min" value={filters.gstMin} onChange={(v) => setFilters({ ...filters, gstMin: v })} testId="revenue-filter-gst-min" />
            <EditableText type="number" className="w-14 !h-8 !border-slate-200 bg-white" placeholder="Max" value={filters.gstMax} onChange={(v) => setFilters({ ...filters, gstMax: v })} testId="revenue-filter-gst-max" />
          </div>
        </FilterField>
        <FilterField label="Status">
          <EditableSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[{ value: "all", label: "All statuses" }, ...INVOICE_STATUSES.map((s) => ({ value: s, label: s }))]} testId="revenue-filter-status" />
        </FilterField>
        <FilterField label="Due date">
          <div className="flex items-center gap-1">
            <FilterDate value={filters.dueFrom} onChange={(v) => setFilters({ ...filters, dueFrom: v })} placeholder="From" testId="revenue-filter-due-from" />
            <FilterDate value={filters.dueTo} onChange={(v) => setFilters({ ...filters, dueTo: v })} placeholder="To" testId="revenue-filter-due-to" />
          </div>
        </FilterField>
        <FilterField label="Compliance (FIRC)">
          <EditableSelect value={filters.compliance} onChange={(v) => setFilters({ ...filters, compliance: v })} options={[{ value: "all", label: "All" }, { value: "missing", label: "FIRC missing" }, { value: "complete", label: "FIRC complete" }]} testId="revenue-filter-compliance" />
        </FilterField>
      </FilterBar>

      <FinanceTotalsBar
        rows={sorted}
        valueFn={quickFilter?.statuses ? (r) => (invoiceRevenueINR(r) || 0) + (invoiceOutstandingINR(r) || 0) : (r) => invoiceRevenueINR(r) || 0}
        label={quickFilter?.label ? `invoices · ${quickFilter.label}` : "invoices"}
        testId="revenue-totals-bar"
      />

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm table-fixed min-w-[2000px]" data-testid="revenue-ledger-table">
          <thead className="bg-slate-50">
            <tr>
              {bulk.canBulk && (
                <th className="px-2 py-2 sticky left-0 z-20 bg-slate-50" style={{ width: STICKY_W.checkbox }}><bulk.HeaderCheckbox /></th>
              )}
              <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50" style={{ left: bulk.canBulk ? STICKY_W.checkbox : 0, width: STICKY_W.date }}>Date</th>
              <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50" style={{ left: (bulk.canBulk ? STICKY_W.checkbox : 0) + STICKY_W.date, width: STICKY_W.invNum }}>Inv #</th>
              <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50" style={{ left: (bulk.canBulk ? STICKY_W.checkbox : 0) + STICKY_W.date + STICKY_W.invNum, width: STICKY_W.client }}>Client</th>
              <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50 shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]" style={{ left: (bulk.canBulk ? STICKY_W.checkbox : 0) + STICKY_W.date + STICKY_W.invNum + STICKY_W.client, width: STICKY_W.amount }}>Amount</th>
              {[["Project", COL_W.project], ["Service Type *", COL_W.service], ["HSN", COL_W.hsn], ["Ccy", COL_W.ccy], ["FX rate", COL_W.fx], ["GST %", COL_W.gst], ["TDS %", COL_W.tds], ["Status", COL_W.status], ["Due", COL_W.due], ["Received Date", COL_W.received], ["Net Received ₹", COL_W.netreceived], ["Net receivable ₹", COL_W.netreceivable], ["Account", COL_W.account], ["FIRC", COL_W.firc], ["Invoice Doc", COL_W.invdoc], ["Actions", COL_W.actions]].map(([h, w]) => (
                <th key={h} className="label-overline px-2 py-2 text-left whitespace-nowrap" style={{ width: w }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((r) => (
              <RevenueLedgerRow
                key={r.id}
                row={r}
                dirty={isDirty(r.id)}
                selected={bulk.isSelected(r.id)}
                canBulk={bulk.canBulk}
                RowCheckbox={bulk.RowCheckbox}
                updateCell={updateCell}
                working={working === r.id}
                isFounder={isFounder}
                clientOptions={clientOptions}
                projectOptions={projectOptions}
                serviceOptions={mains}
                sourceOptions={sourceOptions}
                onStatusChange={handleStatusChange}
                onSubmitForApproval={submitForApproval}
                onApprove={approve}
                onMoveToExpense={moveToExpense}
                onRemove={remove}
                onPreview={onPreview}
                onSaved={onChanged}
              />
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={bulk.canBulk ? 20 : 19} className="px-3 py-6 text-center text-xs text-slate-400">No invoices yet — click "New Invoice" above to add one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
      <DriveLinkPreviewModal
        open={!!fircPreview}
        url={fircPreview?.url}
        title={fircPreview?.title}
        onClose={() => setFircPreview(null)}
      />

      {/* Feb 2026 — True Received Date prompt: fires the moment a row's
          status is flipped to Received/Paid with no date on file yet. */}
      <Dialog open={!!receivedPrompt} onOpenChange={(v) => { if (!v) { setReceivedPrompt(null); setReceivedDateInput(""); } }}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="received-date-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">When did the payment land?</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Marking this invoice {receivedPrompt?.status === "paid" ? "Paid" : "Received"} needs the actual bank-hit date — this drives the ledger's Received Date column and the client's on-time/late badge.</p>
          <div className="mt-2">
            <EditableDate value={receivedDateInput} onChange={setReceivedDateInput} placeholder="Pick the date" testId="received-date-prompt-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setReceivedPrompt(null); setReceivedDateInput(""); }} data-testid="received-date-prompt-cancel">Cancel</Button>
            <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={confirmReceivedPrompt} data-testid="received-date-prompt-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
